import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { getOrders } from 'api/orders';
import { quoteBySsupplierId } from 'api/quotes';
import {
  getCurrentMonthSalesByRestaurant,
  getCurrentMonthProductSales,
} from 'api/reports';
import { HomeLayouts } from '../../components/layouts/HomeLayout';
import Loading from 'components/Loading';

import NavIcon from './components/NavIcon';
import InicioSection from './components/InicioSection';
import ClientesSection from './components/ClientesSection';
import ProductosSection from './components/ProductosSection';
import { NAV_ITEMS, MOCK_SALES } from './constants';
import { getMonthDisplay } from './helpers';
import styles from './summary.module.scss';

const Summary = () => {
  const { user, token, subscription } = useSelector((state) => state.auth);

  const [activeSection, setActiveSection] = useState('inicio');
  const [isLoading, setIsLoading] = useState(true);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [salesData, setSalesData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [ordersCount, setOrdersCount] = useState(0);
  const [quotesCount, setQuotesCount] = useState(0);

  const isPremium = useMemo(() => {
    return (
      subscription &&
      subscription.plan &&
      (subscription.plan.abreviacion === 'pro' ||
        subscription.plan.abreviacion === 'enterprise')
    );
  }, [subscription]);

  // Fetch paralelo de ordenes, cotizaciones y ventas
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);

      const ordersPromise = getOrders(
        { page: 1, limit: 9999, status: 'solicitada' },
        user?.id,
        user?.supplierId,
        token,
      )
        .then((data) => {
          setPendingOrders(data.orders || []);
          setOrdersCount(data.totalOrders || data.orders?.length || 0);
        })
        .catch(() => setPendingOrders([]));

      const quotesPromise = quoteBySsupplierId(
        token,
        user?.id,
        user?.supplierId,
        'Solicitada',
        9999,
        1,
      )
        .then((result) => {
          if (result.ok) {
            setPendingQuotes(result.data.quotes || result.data || []);
            setQuotesCount(
              result.data.totalQuotes ||
                result.data.quotes?.length ||
                result.data?.length ||
                0,
            );
          }
        })
        .catch(() => setPendingQuotes([]));

      const salesPromise = getCurrentMonthSalesByRestaurant(user?.supplierId, token, 10)
        .then((data) => setSalesData(data))
        .catch(() => setSalesData(MOCK_SALES));

      const productsPromise = getCurrentMonthProductSales(user?.supplierId, token, 10)
        .then((data) => setProductsData(data))
        .catch(() => setProductsData(null));

      await Promise.allSettled([
        ordersPromise,
        quotesPromise,
        salesPromise,
        productsPromise,
      ]);
      setIsLoading(false);
    };

    if (user && token) fetchAllData();
  }, [user, token]);

  const sortedClients = useMemo(() => {
    if (!salesData?.clients) return [];
    return [...salesData.clients].sort((a, b) => b.total - a.total);
  }, [salesData]);

  const sortedProducts = useMemo(() => {
    if (!productsData?.products) return [];
    return [...productsData.products].sort((a, b) => b.total - a.total);
  }, [productsData]);

  const totalProductSales = useMemo(() => {
    return productsData?.total || sortedProducts.reduce((sum, p) => sum + p.total, 0);
  }, [productsData, sortedProducts]);

  const handleNavChange = useCallback((sectionId) => {
    setActiveSection(sectionId);
  }, []);

  if (isLoading && !salesData) {
    return (
      <HomeLayouts>
        <div className={styles.loadingWrap}>
          <Loading />
        </div>
      </HomeLayouts>
    );
  }

  return (
    <HomeLayouts>
      <div className={styles.dashboard}>
        <header className={styles.dashHeader}>
          <div className={styles.dashHeaderInner}>
            <div className={styles.dashHeaderText}>
              <h1 className={styles.dashTitle}>Resumen</h1>
              <p className={styles.dashSubtitle}>Panel de control de tu negocio</p>
            </div>
            <div className={styles.dashHeaderMeta}>
              <span className={styles.monthBadge}>
                {salesData ? getMonthDisplay(salesData.month) : ''}
              </span>
            </div>
          </div>
        </header>

        {/* Sub-navegacion con indicador deslizante */}
        <div className={styles.navContainer}>
          <nav className={styles.subNav}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                className={`${styles.navItem} ${
                  activeSection === item.id ? styles.navItemActive : ''
                }`}
                onClick={() => handleNavChange(item.id)}
              >
                <span className={styles.navIcon}>
                  <NavIcon type={item.icon} size={15} />
                </span>
                <span className={styles.navLabel}>{item.label}</span>
                {item.premium && !isPremium && (
                  <span className={styles.navLock}>
                    <NavIcon type="lock" />
                  </span>
                )}
              </button>
            ))}
            <div
              className={styles.navIndicator}
              style={{
                transform: `translateX(${
                  NAV_ITEMS.findIndex((i) => i.id === activeSection) * 100
                }%)`,
                width: `${100 / NAV_ITEMS.length}%`,
              }}
            />
          </nav>
        </div>

        <div className={styles.content}>
          {activeSection === 'inicio' && (
            <InicioSection
              salesData={salesData}
              ordersCount={ordersCount}
              quotesCount={quotesCount}
              pendingOrders={pendingOrders}
              pendingQuotes={pendingQuotes}
              sortedClients={sortedClients}
              isPremium={isPremium}
              onNavChange={handleNavChange}
            />
          )}

          {activeSection === 'clientes' && (
            <ClientesSection
              salesData={salesData}
              sortedClients={sortedClients}
              isPremium={isPremium}
            />
          )}

          {activeSection === 'productos' && (
            <ProductosSection
              productsData={productsData}
              sortedProducts={sortedProducts}
              totalProductSales={totalProductSales}
              isPremium={isPremium}
            />
          )}
        </div>
      </div>
    </HomeLayouts>
  );
};

export default Summary;
